import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Header from "../components/Header";
import RunHistoryList from "../components/RunHistoryList";
import { useResearchQuota } from "../context/QuotaContext";
import { ApiError, HTTP_STATUS_CONFLICT, deleteRun, fetchRuns } from "../lib/api";
import type { RunSummary } from "../types";

const RUNS_PAGE_LIMIT = 20;
const GENERIC_LOAD_ERROR = "Falha ao carregar o histórico de pesquisas.";
const GENERIC_LOAD_MORE_ERROR = "Falha ao carregar mais pesquisas.";
const RUN_STILL_PROCESSING_ERROR =
  "A pesquisa ainda está em processamento e não pode ser excluída.";
const GENERIC_DELETE_ERROR = "Falha ao excluir a pesquisa.";

export default function HistoryPage() {
  const navigate = useNavigate();
  const { refresh: refreshQuota } = useResearchQuota();

  const [items, setItems] = useState<RunSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadFirstPage() {
      setIsLoading(true);
      setError(null);
      try {
        const page = await fetchRuns(null, RUNS_PAGE_LIMIT);
        if (!active) return;
        setItems(page.items);
        setNextCursor(page.next_cursor);
      } catch (err) {
        console.error("Falha ao carregar o histórico de pesquisas:", err);
        if (active) setError(GENERIC_LOAD_ERROR);
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadFirstPage();
    return () => {
      active = false;
    };
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor) return;
    setIsLoadingMore(true);
    setError(null);
    try {
      const page = await fetchRuns(nextCursor, RUNS_PAGE_LIMIT);
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (err) {
      console.error("Falha ao carregar mais pesquisas:", err);
      setError(GENERIC_LOAD_MORE_ERROR);
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextCursor]);

  const handleOpen = useCallback(
    (id: string) => {
      navigate(`/history/${id}`);
    },
    [navigate]
  );

  const handleConfirmDelete = useCallback(
    async (id: string) => {
      setError(null);
      try {
        await deleteRun(id);
        setItems((prev) => prev.filter((item) => item.id !== id));
        void refreshQuota();
      } catch (err) {
        console.error("Falha ao excluir a pesquisa:", err);
        if (err instanceof ApiError && err.status === HTTP_STATUS_CONFLICT) {
          setError(RUN_STILL_PROCESSING_ERROR);
        } else {
          setError(GENERIC_DELETE_ERROR);
        }
      } finally {
        setPendingDeleteId(null);
      }
    },
    [refreshQuota]
  );

  const handleCancelDelete = useCallback(() => setPendingDeleteId(null), []);

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "var(--color-background)" }}>
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/tavily_landscapes_edited_11.webp)",
          opacity: 0.7,
          willChange: "transform",
          transform: "translateZ(0)",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-8">
        <Header />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-10 space-y-4"
        >
          <h1 className="font-display text-xl text-ink-100">Histórico de pesquisas</h1>

          {error && (
            <div
              role="alert"
              className="rounded-xl border border-red-300/60 bg-red-50/80 px-4 py-3 font-body"
            >
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="glass rounded-2xl p-10 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-ink-400 animate-spin" />
            </div>
          ) : (
            <>
              <RunHistoryList
                items={items}
                onOpen={handleOpen}
                onDelete={setPendingDeleteId}
                pendingDeleteId={pendingDeleteId}
                onConfirmDelete={handleConfirmDelete}
                onCancelDelete={handleCancelDelete}
              />

              {nextCursor && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="px-4 py-2 rounded-lg glass-button text-ink-200 text-sm font-medium font-body transition-all disabled:opacity-60"
                  >
                    {isLoadingMore ? "Carregando..." : "Carregar mais"}
                  </button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
